<script setup lang="ts">
import { useBillingUtils } from '@/use/billingUtils';
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import {
  discountDisplayName,
  isUnexpandedDiscountPlaceholder,
  pickDiscountFromSources,
  resolveCouponFromDiscountRow,
} from '@/lib/stripeDiscounts';
import { findCouponPlanMismatch } from '@/lib/couponPlans';
import type { PlanProduct } from '@/types/billing';
import { Alert } from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import BillingCouponPlanNotice from '@/components/BillingCouponPlanNotice/BillingCouponPlanNotice.vue';

const props = defineProps<{ class?: string }>();

const billingStore = useBillingStore();
const { couponDiscountBenefit, discountEnd, formatPrice } = useBillingUtils();
const { t } = useI18n();
const router = useRouter();

const discount = computed((): Record<string, unknown> | null => {
  const sub = billingStore.primarySubscription as Record<string, unknown> | null;
  const customer = billingStore.customer as Record<string, unknown> | null;
  const invoice = billingStore.upcomingInvoice as Record<string, unknown> | null;
  return pickDiscountFromSources([sub, customer, invoice]);
});

interface AccountCreditInfo {
  /** Always positive cents — magnitude of credit being added or applied. */
  amount: number;
  currency: string;
  /** 'pending' = will be added to the customer's balance (e.g. proration credit on next invoice).
   *  'available' = already on the customer's balance, will be applied against future charges. */
  mode: 'pending' | 'available';
}

const accountCredit = computed<AccountCreditInfo | null>(() => {
  const customer = billingStore.customer as Stripe.Customer | null;
  const invoice = billingStore.upcomingInvoice as Stripe.Invoice | null;
  const currency = (customer?.currency || invoice?.currency || 'usd') as string;
  const customerBalance = customer?.balance ?? 0;

  // Negative-total invoice: the upcoming invoice is purely credit being issued
  // (e.g. proration refund from a plan downgrade) — Stripe will add it to
  // customer.balance when the invoice posts.
  if (invoice && typeof invoice.total === 'number' && invoice.total < 0) {
    return { amount: -invoice.total, currency, mode: 'pending' };
  }

  // Standing credit on the customer that hasn't been applied yet.
  if (customerBalance < 0) {
    return { amount: -customerBalance, currency, mode: 'available' };
  }

  // Invoice that draws down existing customer credit (starting_balance was negative).
  if (invoice && typeof invoice.starting_balance === 'number' && invoice.starting_balance < 0) {
    return { amount: -invoice.starting_balance, currency, mode: 'available' };
  }

  return null;
});

const accountCreditSummary = computed(() => {
  const credit = accountCredit.value;
  if (!credit) return '';
  const formatted = formatPrice(credit.amount, credit.currency);
  return credit.mode === 'pending'
    ? t('billing_discount_account_credit_pending', { amount: formatted })
    : t('billing_discount_account_credit', { amount: formatted });
});

const couponObject = computed((): Stripe.Coupon | null => {
  const raw = discount.value;
  if (!raw || isUnexpandedDiscountPlaceholder(raw)) return null;
  return resolveCouponFromDiscountRow(raw);
});

const couponDisplayName = computed(() => discountDisplayName(discount.value));

/** One sentence: “Name: benefit” when both exist; otherwise the benefit or name alone. */
const discountAlertSummary = computed(() => {
  const c = couponObject.value;
  if (!c) return '';
  const benefit = couponDiscountBenefit(c);
  const name = couponDisplayName.value.trim();
  if (name && benefit) return t('billing_discount_alert_line', { name, benefit });
  if (benefit) return benefit;
  return name;
});

const alertTitle = computed(() => {
  if (couponObject.value) return t('billing_coupon');
  const name = couponDisplayName.value;
  if (name) return `${t('billing_coupon')}: ${name}`;
  return t('billing_coupon');
});

const className = computed(() => props.class || '');

const discountForEnd = computed(() => discount.value as unknown as Stripe.Discount);

const currentPlan = computed(() => billingStore.primarySubscription?.plan);
const currentProductId = computed(() => (billingStore.isLifetime ? undefined : currentPlan.value?.product?.id));
const currentPlanName = computed(() => currentPlan.value?.product?.name || currentPlan.value?.name || '');
const currentInterval = computed(() => (currentPlan.value?.interval === 'year' ? 'year' : 'month'));

// The code on the account can't discount the plan they're on: the coupon box becomes a warning.
// Only the account's own coupons count here; a code waiting for checkout isn't in this box.
const planMismatch = computed(() =>
  findCouponPlanMismatch(
    billingStore.accountScopedCoupons,
    currentProductId.value,
    billingStore.currentOfferingProducts ?? billingStore.offerings?.products,
  ),
);

function switchToCouponPlan(plan: PlanProduct) {
  if (billingStore.selectPlanForCheckout(plan, currentInterval.value)) router.push('/billing/checkout');
}

const showRepeatingTimeLeft = computed(() => {
  if (!couponObject.value || couponObject.value.duration !== 'repeating') return false;
  return Boolean(discountEnd(discountForEnd.value));
});
</script>

<template>
  <Alert
    v-if="discount"
    :variant="planMismatch ? 'warning' : 'info'"
    :title="alertTitle"
    :class="className"
    :icon="Boolean(planMismatch)"
  >
    <template v-if="couponObject">
      <p class="billing-discount-alert__summary">{{ discountAlertSummary }}</p>
      <p v-if="showRepeatingTimeLeft" class="billing-discount-alert__time-left">
        {{
          t('billing_discount_time_left', {
            time: discountEnd(discountForEnd),
          })
        }}
      </p>
    </template>
    <template v-else>
      {{ t('billing_coupon_applied_short') }}
    </template>
    <BillingCouponPlanNotice
      embedded
      :coupons="billingStore.accountScopedCoupons"
      :product-id="currentProductId"
      :plan-name="currentPlanName"
      :interval="currentInterval"
      @switch="switchToCouponPlan"
      @change="router.push({ name: 'billing-plans' })"
    />
  </Alert>
  <Alert v-if="accountCredit" variant="info" :class="className" :icon="false">
    <strong>{{ t('billing_balance_credit_title') }}:</strong> {{ accountCreditSummary }}
  </Alert>
</template>

<style lang="scss" scoped>
.billing-discount-alert__summary {
  margin: 0;
}

.billing-discount-alert__time-left {
  margin: space('xsmall') 0 0;
}
</style>
